import { useCallback, useEffect, useRef } from 'react';
const TYPEAHEAD_RESET_MS = 350;
/** Frames the virtual list gets to render a scrolled-to option before we stop chasing it. */
const RENDER_SETTLE_FRAMES = 3;
/** First enabled option at or after `start`, walking by `step`. `-1` when there is none. */
const findEnabledIndex = (options, start, step) => {
    for (let index = start; index >= 0 && index < options.length; index += step) {
        if (!options[index].isDisabled) {
            return index;
        }
    }
    return -1;
};
const resolveTargetIndex = (key, options, current) => {
    const last = options.length - 1;
    switch (key) {
        case 'ArrowDown':
            return findEnabledIndex(options, current + 1, 1);
        case 'ArrowUp':
            return current < 0 ? findEnabledIndex(options, last, -1) : findEnabledIndex(options, current - 1, -1);
        case 'Home':
        case 'PageUp':
            return findEnabledIndex(options, 0, 1);
        case 'End':
        case 'PageDown':
            return findEnabledIndex(options, last, -1);
        default:
            return null;
    }
};
/**
 * Ref callback that disables an option's own `scrollIntoView`, which is the only way to stop Headless UI from scrolling
 * it: `ListboxOption` reads the method off the element (`domRef.current?.scrollIntoView?.({ block: 'nearest' })`) with
 * no opt-out prop.
 *
 * Required in the virtualized path. Headless UI's `UnregisterOptions` splices unmounted options out of its array
 * without adjusting `activeOptionIndex`, and forces `activationTrigger` back to `Other` — which re-enables that
 * scroll. So every time the virtual list unmounts an option above the active one, a *different*, lower option becomes
 * active and scrolls itself into view, unmounting more options above it: the dropdown runs to the end of the list.
 * Suppressing the scroll breaks the loop, and focusing the intended option re-syncs `activeOptionIndex` by id.
 */
export const disableOptionAutoScroll = (element) => {
    if (element) {
        element.scrollIntoView = () => undefined;
    }
};
const findOptionElement = (scrollParent, value) => Array.from(scrollParent.querySelectorAll('[role="option"][data-value]')).find((element) => element.dataset['value'] === value);
/**
 * Scrolls the listbox the minimum amount that brings `index` into view, estimating the option's position from a
 * currently rendered one — `aria-posinset` carries its index in the full set. Options share a height, so stepping past
 * the rendered window moves by exactly one option, and a distant jump lands close enough for the virtual list to
 * render the target, which is then positioned precisely by focusing it.
 *
 * Virtuoso's own `scrollToIndex`/`scrollIntoView` are deliberately avoided: they measure against a viewport the
 * component derives itself, which does not match a `customScrollParent` dropdown.
 */
const scrollOptionIntoView = (scrollParent, index) => {
    const rendered = scrollParent.querySelector('[role="option"][aria-posinset]');
    if (!rendered) {
        return;
    }
    const renderedIndex = Number(rendered.getAttribute('aria-posinset')) - 1;
    const { height: optionHeight, top: renderedTop } = rendered.getBoundingClientRect();
    const targetTop = renderedTop - scrollParent.getBoundingClientRect().top + (index - renderedIndex) * optionHeight;
    const overflowBelow = targetTop + optionHeight - scrollParent.clientHeight;
    if (targetTop < 0) {
        scrollParent.scrollTop += targetTop;
    }
    else if (overflowBelow > 0) {
        scrollParent.scrollTop += overflowBelow;
    }
};
/**
 * Owns listbox keyboard navigation for the virtualized Select path. Headless UI only registers rendered options, so
 * the target is resolved against the full options array and the listbox is scrolled until that option renders;
 * focusing it then hands the active state back to Headless UI, whose `ListboxOption` activates on focus.
 */
export const useSelectVirtualKeyboard = ({ enabled, options, scrollParent, searchableOptions, }) => {
    const activeValueRef = useRef(null);
    const typeaheadRef = useRef('');
    const typeaheadTimeoutRef = useRef(null);
    const clearTypeahead = useCallback(() => {
        typeaheadRef.current = '';
        if (typeaheadTimeoutRef.current) {
            clearTimeout(typeaheadTimeoutRef.current);
            typeaheadTimeoutRef.current = null;
        }
    }, []);
    useEffect(() => clearTypeahead, [clearTypeahead]);
    const goToIndex = useCallback((index, { focus }) => {
        const option = options[index];
        if (!option || !scrollParent) {
            return;
        }
        activeValueRef.current = option.value;
        /**
         * `scrollOptionIntoView` is the only thing that scrolls (it no-ops once the option is inside the viewport), and
         * focusing uses `preventScroll` — Headless UI's and the browser's own scrolling are both suppressed so they
         * cannot fight it.
         */
        const reveal = (framesLeft) => {
            if (activeValueRef.current !== option.value) {
                return;
            }
            scrollOptionIntoView(scrollParent, index);
            const target = findOptionElement(scrollParent, option.value);
            // Focusing hands the active state back to Headless UI, which resolves `activeOptionIndex` from the option's id.
            if (target) {
                if (focus) {
                    target.focus({ preventScroll: true });
                }
                return;
            }
            if (framesLeft > 0) {
                requestAnimationFrame(() => reveal(framesLeft - 1));
            }
        };
        reveal(RENDER_SETTLE_FRAMES);
    }, [options, scrollParent]);
    const handleKeyDownCapture = useCallback((event) => {
        var _a;
        if (!enabled || options.length === 0 || event.target.closest('input')) {
            return;
        }
        const currentIndex = options.findIndex((option) => option.value === activeValueRef.current);
        const targetIndex = resolveTargetIndex(event.key, options, currentIndex);
        if (targetIndex !== null) {
            event.preventDefault();
            event.stopPropagation();
            clearTypeahead();
            goToIndex(targetIndex, { focus: true });
            return;
        }
        const isTypeaheadKey = !searchableOptions && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
        // Space only starts a query mid-word; on its own it stays "select the active option".
        if (!isTypeaheadKey || (event.key === ' ' && !typeaheadRef.current)) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        typeaheadRef.current += event.key.toLowerCase();
        clearTimeout((_a = typeaheadTimeoutRef.current) !== null && _a !== void 0 ? _a : undefined);
        typeaheadTimeoutRef.current = setTimeout(clearTypeahead, TYPEAHEAD_RESET_MS);
        const matchIndex = options.findIndex((option) => !option.isDisabled && option.label.toLowerCase().startsWith(typeaheadRef.current));
        goToIndex(matchIndex, { focus: true });
    }, [enabled, options, searchableOptions, clearTypeahead, goToIndex]);
    /**
     * Brings the selected option into view when the dropdown opens, without taking focus — the search field keeps it,
     * and Headless UI highlights the option once the user starts navigating.
     */
    const revealOption = useCallback((value) => {
        clearTypeahead();
        activeValueRef.current = value !== null && value !== void 0 ? value : null;
        if (!enabled || !value) {
            return;
        }
        requestAnimationFrame(() => goToIndex(options.findIndex((option) => option.value === value), { focus: false }));
    }, [enabled, options, clearTypeahead, goToIndex]);
    return {
        handleKeyDownCapture,
        revealOption,
    };
};
//# sourceMappingURL=use_select_virtual_keyboard.hook.js.map